/*eslint max-lines-per-function: ["error", 620]*/
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  CreateUpdateExamDto,
  GetCriticalPointDetailsDto,
  GetExamDto,
  GetLastEditorDto,
  GetSupportingExaminationPreviewDto,
  SupportingExaminationPreviewRequestDto,
} from '../autogenerated/spdApiClient';
import { examForGrid } from '../../../spd/components/Exam/ExamSearch';
import {
  CreateUpdateExamWithTestsDto,
  CreateUpdateExamWithTestsDtoFromJSON,
} from '../autogenerated/spdApiClient/models/CreateUpdateExamWithTestsDto';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { ArticleMode, ExamPaymentSchema } from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';

export type ExamEntity = GetExamDto;

export interface ExamContextInterface {
  getExam: (id: string) => Promise<ExamEntity>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    mode?: ArticleMode,
    onlyActive?: boolean,
    companyId?: string,
    dateFrom?: string,
    dateTo?: string,
    withoutInvoiceAndReceipt?: boolean,
    completed?: boolean,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
    onlyActive?: boolean,
    companyId?: string,
    dateFrom?: string,
    dateTo?: string,
    withoutInvoiceAndReceipt?: boolean,
    completed?: boolean,
  ) => Promise<number>;
  getAllIds: (
    filterText?: string,
    filterColumns?: string,
    onlyActive?: boolean,
    companyId?: string,
    dateFrom?: string,
    dateTo?: string,
    withoutInvoiceAndReceipt?: boolean,
    completed?: boolean,
  ) => Promise<string[]>;
  getAllByTestByPortion: (
    testId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    completed?: boolean,
  ) => Promise<GridRecord[]>;
  getAllByTestCount: (
    testId: string,
    filterText?: string,
    filterColumns?: string,
    completed?: boolean,
  ) => Promise<number>;
  getWithExaminedByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    mode?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getWithExaminedCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
  ) => Promise<number>;
  getWithoutJudgmentByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    mode?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getWithoutJudgmentCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
  ) => Promise<number>;
  getWithoutJudgmentWithoutReferralsByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    mode?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getWithoutJudgmentWithoutReferralsCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
  ) => Promise<number>;
  getForAutomaticJudgmentByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getForAutomaticJudgmentCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getExamsWithAutoJudgmentWithoutPaymentByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getExamsWithAutoJudgmentWithoutPaymentCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getBlockedByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    type?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getBlockedCount: (
    filterText?: string,
    filterColumns?: string,
    type?: ArticleMode,
  ) => Promise<number>;
  getForSpecialistByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    mode?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getForSpecialistCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
  ) => Promise<number>;
  getActiveByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    mode?: ArticleMode,
  ) => Promise<GridRecord[]>;
  getActiveCount: (
    filterText?: string,
    filterColumns?: string,
    additionalId?: string,
    mode?: ArticleMode,
  ) => Promise<number>;
  getForPackageByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    examPackageId?: string,
  ) => Promise<GridRecord[]>;
  getForPackageCount: (
    filterText?: string,
    examPackageId?: string,
  ) => Promise<number>;
  getForExaminedByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    examinedId?: string,
  ) => Promise<GridRecord[]>;
  getForExaminedCount: (
    filterText?: string,
    examinedId?: string,
  ) => Promise<number>;
  getForSalesInvoiceByPortion: (
    salesInvoiceId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    onRemove?: (key: string) => void,
  ) => Promise<GridRecord[]>;
  getForSalesInvoiceCount: (
    filterText?: string,
    salesInvoiceId?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdateExamDto) => Promise<void>;
  createWithTests: (dto: CreateUpdateExamWithTestsDto) => Promise<GetExamDto[]>;
  updateWithTests: (
    examId: string,
    dto: CreateUpdateExamWithTestsDto,
  ) => Promise<GetExamDto>;
  changeDates: (id: string, endDate: Date, startDate?: Date) => Promise<void>;
  changeToInactive: (id: string) => Promise<void>;
  changeAllCards: () => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  setOnline: (id: string) => Promise<void>;
  getOnlineWithoutJudgmentByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getOnlineWithoutJudgmentCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getOnlineAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getOnlineAllCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getInFacilityByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    healthcareFacilityId?: string,
  ) => Promise<GridRecord[]>;
  getInFacilityCount: (
    filterText?: string,
    filterColumns?: string,
    healthcareFacilityId?: string,
  ) => Promise<number>;
  getInFacilityWithoutJudgmentByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    healthcareFacilityId?: string,
  ) => Promise<GridRecord[]>;
  getInFacilityWithoutJudgmentCount: (
    filterText?: string,
    filterColumns?: string,
    healthcareFacilityId?: string,
  ) => Promise<number>;
  getCriticalPointDetails: (id: string) => Promise<GetCriticalPointDetailsDto>;
  getSupportingExaminationPreview: (
    dto: SupportingExaminationPreviewRequestDto,
  ) => Promise<GetSupportingExaminationPreviewDto>;
  getSupportingExaminationPreviewByExam: (
    examId: string,
  ) => Promise<GetSupportingExaminationPreviewDto>;
}

export const ExamContext = (api: DefaultApi) => {
  return {
    getExam: async (id: string) => {
      return await api.examControllerGet(id);
    },

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      mode?: ArticleMode,
      onlyActive?: boolean,
      companyId?: string,
      dateFrom?: string,
      dateTo?: string,
      withoutInvoiceAndReceipt?: boolean,
      completed?: boolean,
    ) => {
      const data = await api.examControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        mode,
        undefined,
        onlyActive?.toString(),
        undefined,
        undefined,
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt?.toString(),
        completed?.toString(),
      );
      return data.map(exam => examForGrid(exam));
    },

    getAllCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
      onlyActive?: boolean,
      companyId?: string,
      dateFrom?: string,
      dateTo?: string,
      withoutInvoiceAndReceipt?: boolean,
      completed?: boolean,
    ) =>
      api.examControllerGetAllCount(
        filterText || '',
        filterColumns || '',
        mode,
        undefined,
        onlyActive?.toString(),
        undefined,
        undefined,
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt?.toString(),
        completed?.toString(),
      ),

    getAllIds: (
      filterText?: string,
      filterColumns?: string,
      onlyActive?: boolean,
      companyId?: string,
      dateFrom?: string,
      dateTo?: string,
      withoutInvoiceAndReceipt?: boolean,
      completed?: boolean,
    ) =>
      api.examControllerGetAllIds(
        filterText || '',
        filterColumns || '',
        onlyActive?.toString(),
        companyId,
        dateFrom,
        dateTo,
        withoutInvoiceAndReceipt?.toString(),
        completed?.toString(),
      ),

    getAllByTestByPortion: async (
      testId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      completed?: boolean,
    ) => {
      const data = await api.examControllerGetAllByTestByPortion(
        testId,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        completed?.toString(),
      );
      return data.map(exam => examForGrid(exam));
    },

    getAllByTestCount: (
      testId: string,
      filterText?: string,
      filterColumns?: string,
      completed?: boolean,
    ) =>
      api.examControllerGetAllByTestCount(
        testId,
        filterText || '',
        filterColumns || '',
        completed?.toString(),
      ),

    getWithExaminedByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      mode?: ArticleMode,
    ) => {
      const data = await api.examControllerGetWithExaminedByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        mode,
      );
      return data.map(exam => examForGrid(exam));
    },

    getWithExaminedCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
    ) =>
      api.examControllerGetWithExaminedCount(
        filterText || '',
        filterColumns || '',
        mode,
      ),

    getActiveByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      mode?: ArticleMode,
    ) => {
      const data = await api.examControllerGetActiveByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        mode,
      );
      return data.map(exam => examForGrid(exam));
    },

    getActiveCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
    ) => api.examControllerGetActiveCount(filterText || '', mode),

    getWithoutJudgmentByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      mode?: ArticleMode,
    ) => {
      const data = await api.examControllerGetWithoutJudgmentByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        mode,
      );
      return data.map(exam => examForGrid(exam));
    },

    getWithoutJudgmentCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
    ) =>
      api.examControllerGetWithoutJudgmentCount(
        filterText || '',
        filterColumns || '',
        mode,
      ),

    getWithoutJudgmentWithoutReferralsByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      mode?: ArticleMode,
    ) => {
      const data =
        await api.examControllerGetWithoutJudgmentWithoutReferralsByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns || '',
          mode,
        );
      return data.map(exam => examForGrid(exam));
    },

    getWithoutJudgmentWithoutReferralsCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
    ) =>
      api.examControllerGetWithoutJudgmentWithoutReferralsCount(
        filterText || '',
        filterColumns || '',
        mode,
      ),

    getForAutomaticJudgmentByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.examControllerGetForAutomaticJudgmentByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(exam => examForGrid(exam));
    },

    getForAutomaticJudgmentCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.examControllerGetForAutomaticJudgmentCount(
        filterText || '',
        filterColumns || '',
      ),

    getExamsWithAutoJudgmentWithoutPaymentByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.examControllerGetExamsWithAutoJudgmentWithoutPaymentByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns || '',
        );
      return data.map(exam => examForGrid(exam));
    },

    getExamsWithAutoJudgmentWithoutPaymentCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.examControllerGetExamsWithAutoJudgmentWithoutPaymentCount(
        filterText || '',
        filterColumns || '',
      ),

    getBlockedByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      type?: ArticleMode,
    ) => {
      const data = await api.examControllerGetBlockedByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        type,
      );
      return data.map(exam => examForGrid(exam));
    },

    getBlockedCount: (
      filterText?: string,
      filterColumns?: string,
      type?: ArticleMode,
    ) =>
      api.examControllerGetBlockedCount(
        filterText || '',
        filterColumns || '',
        type,
      ),

    getForSpecialistByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      mode?: ArticleMode,
    ) => {
      const data = await api.examControllerGetForSpecialistByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        mode,
      );
      return data.map(exam => examForGrid(exam));
    },

    getForSpecialistCount: (
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
      mode?: ArticleMode,
    ) =>
      api.examControllerGetForSpecialistCount(
        filterText || '',
        filterColumns || '',
        mode,
      ),

    getForPackageByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      examPackageId?: string,
    ) => {
      const data = await api.examControllerGetForPackageByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        examPackageId || '',
      );
      return data.map(exam => examForGrid(exam));
    },

    getForPackageCount: (filterText?: string, examPackageId?: string) =>
      api.examControllerGetForPackageCount(
        filterText || '',
        examPackageId || '',
      ),

    getForExaminedByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      examinedId?: string,
    ) => {
      const data = await api.examControllerGetForExaminedByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        examinedId || '',
      );
      return data.map(exam => examForGrid(exam));
    },

    getForExaminedCount: (filterText?: string, examinedId?: string) =>
      api.examControllerGetForPackageCount(filterText || '', examinedId || ''),

    getForSalesInvoiceByPortion: async (
      salesInvoiceId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      onRemove?: (key: string) => void,
    ) => {
      const data = await api.examControllerGetForSalesInvoiceByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        salesInvoiceId,
      );
      return data.map(exam => examForGrid(exam, onRemove));
    },

    getForSalesInvoiceCount: (filterText?: string, salesInvoiceId?: string) =>
      api.examControllerGetForSalesInvoiceCount(
        filterText || '',
        salesInvoiceId || '',
      ),

    update: (id: string, dto: CreateUpdateExamDto) =>
      api.examControllerUpdate(id, dto),

    createWithTests: (dto: CreateUpdateExamWithTestsDto) =>
      api.examControllerCreateWithTests(dto),

    updateWithTests: (examId: string, dto: CreateUpdateExamWithTestsDto) =>
      api.examControllerUpdateWithTests(examId, dto),

    changeDates: (id: string, endDate: Date, startDate?: Date) =>
      api.examControllerChangeDates(id, {
        startDate: startDate,
        endDate: endDate,
      }),

    changeToInactive: (id: string) => api.examControllerChangeToInactive(id),
    changeAllCards: () => api.pdfPrinterControllerChangeAllCards(),
    getLastEditor: (id: string) => api.examControllerGetLastEditor(id),
    setOnline: (id: string) => api.examControllerSetOnline(id),

    getOnlineWithoutJudgmentByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.examControllerGetWithoutJudgmentByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        undefined,
        true,
      );
      return data.map(exam => examForGrid(exam));
    },

    getOnlineWithoutJudgmentCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.examControllerGetWithoutJudgmentCount(
        filterText || '',
        filterColumns || '',
        undefined,
        true,
      ),

    getOnlineAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.examControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        undefined,
        true,
      );
      return data.map(exam => examForGrid(exam));
    },

    getOnlineAllCount: (filterText?: string, filterColumns?: string) =>
      api.examControllerGetAllCount(
        filterText || '',
        filterColumns || '',
        undefined,
        true,
      ),

    getInFacilityByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      healthcareFacilityId?: string,
    ) => {
      const data = await api.examControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        undefined,
        undefined,
        undefined,
        'true',
        healthcareFacilityId || '',
      );
      return data.map(exam => examForGrid(exam));
    },

    getInFacilityCount: (
      filterText?: string,
      filterColumns?: string,
      healthcareFacilityId?: string,
    ) =>
      api.examControllerGetAllCount(
        filterText || '',
        filterColumns || '',
        undefined,
        undefined,
        undefined,
        'true',
        healthcareFacilityId || '',
      ),

    getInFacilityWithoutJudgmentByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      healthcareFacilityId?: string,
    ) => {
      const data = await api.examControllerGetWithoutJudgmentByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
        undefined,
        undefined,
        'true',
        healthcareFacilityId || '',
      );
      return data.map(exam => examForGrid(exam));
    },

    getInFacilityWithoutJudgmentCount: (
      filterText?: string,
      filterColumns?: string,
      healthcareFacilityId?: string,
    ) =>
      api.examControllerGetWithoutJudgmentCount(
        filterText || '',
        filterColumns || '',
        undefined,
        undefined,
        'true',
        healthcareFacilityId || '',
      ),

    getCriticalPointDetails: (id: string) =>
      api.examControllerGetCriticalPointDetails(id),

    getSupportingExaminationPreview: (
      dto: SupportingExaminationPreviewRequestDto,
    ) => api.examControllerGetSupportingExaminationPreview(dto),

    getSupportingExaminationPreviewByExam: (examId: string) =>
      api.examControllerGetSupportingExaminationPreviewByExam(examId),
  };
};

export const validatePayment = (exam: CreateUpdateExamDto) =>
  validateAgainst(ExamPaymentSchema, exam);

export const examData = (exam?: GetExamDto): CreateUpdateExamWithTestsDto => {
  return CreateUpdateExamWithTestsDtoFromJSON({
    ...exam,
    healthcareFacilityId: exam?.examPackage?.healthcareFacility?.id,
    examPackageId: exam?.examPackage?.id,
  });
};
